import React from 'react';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';

const ScrollButtons: React.FC = () => {
    // Helper to get current scroll position across all browsers/containers
    const getScrollTop = (): number => {
        return window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    };

    // Helper to get total scrollable height of the webpage
    const getScrollHeight = (): number => {
        return Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
            document.body.offsetHeight,
            document.documentElement.offsetHeight,
            document.body.clientHeight,
            document.documentElement.clientHeight
        );
    };

    // Universal custom smooth scroll animation (duration: 1000ms)
    const smoothScrollTo = (targetPosition: number, duration: number = 1000) => {
        const startPosition = getScrollTop();
        const distance = targetPosition - startPosition;
        if (Math.abs(distance) < 5) return;
        let startTime: number | null = null;

        const easeInOutCubic = (t: number): number => {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        };

        const animation = (currentTime: number) => {
            if (startTime === null) startTime = currentTime;
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);
            const easeProgress = easeInOutCubic(progress);
            const currentPos = startPosition + distance * easeProgress;

            window.scrollTo(0, currentPos);
            document.documentElement.scrollTop = currentPos;
            document.body.scrollTop = currentPos;

            if (timeElapsed < duration) {
                requestAnimationFrame(animation);
            }
        };

        requestAnimationFrame(animation);
    };

    const handleScrollTop = () => {
        smoothScrollTo(0, 1000);
    };

    const handleScrollBottom = () => {
        const totalHeight = getScrollHeight();
        const viewportHeight = window.innerHeight;
        const maxScroll = totalHeight - viewportHeight;
        smoothScrollTo(maxScroll > 0 ? maxScroll : totalHeight, 1000);
    };

    const [isVisible, setIsVisible] = React.useState(true);

    React.useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'style') {
                    const overflow = document.body.style.overflow;
                    setIsVisible(overflow !== 'hidden');
                }
            });
        });

        observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
        
        // Initial check
        if (document.body.style.overflow === 'hidden') {
            setIsVisible(false);
        }

        return () => observer.disconnect();
    }, []);

    if (!isVisible) return null;

    return (
        <div className="hidden md:flex fixed bottom-6 right-5 z-[9999] flex-col items-center bg-white border-2 border-[#01bdc0] rounded-full shadow-[0_4px_16px_rgba(1,189,192,0.35)] p-1 gap-1">
            {/* Scroll to Top Button */}
            <div className="relative flex items-center group">
                <button
                    onClick={handleScrollTop}
                    aria-label="Scroll to top"
                    title="Scroll to Top"
                    className="w-[32px] h-[32px] rounded-full text-[#01bdc0] hover:bg-[#01bdc0] hover:text-white flex items-center justify-center cursor-pointer transition-all duration-200 outline-none active:scale-90"
                >
                    <FaChevronUp className="w-[13px] h-[13px] text-current" />
                </button>
                <span className="absolute right-[44px] bg-[#172337] text-white px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap opacity-0 invisible translate-x-2 transition-all duration-200 pointer-events-none  hidden md:block group-hover:opacity-100 group-hover:visible group-hover:translate-x-0 after:content-[''] after:absolute after:-right-1 after:top-1/2 after:-translate-y-1/2 after:border-y-4 after:border-y-transparent after:border-l-[5px] after:border-l-[#172337]">
                    Scroll to Top
                </span>
            </div>

            {/* Divider */}
            <div className="w-5 h-[1px] bg-[#01bdc0]/30 rounded-full" />

            {/* Scroll to Bottom Button */}
            <div className="relative flex items-center group">
                <button
                    onClick={handleScrollBottom}
                    aria-label="Scroll to bottom"
                    title="Scroll to Bottom"
                    className="w-[32px] h-[32px] rounded-full text-[#01bdc0] hover:bg-[#01bdc0] hover:text-white flex items-center justify-center cursor-pointer transition-all duration-200 outline-none active:scale-90"
                >
                    <FaChevronDown className="w-[13px] h-[13px] text-current" />
                </button>
                <span className="absolute right-[44px] bg-[#172337] text-white px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap opacity-0 invisible translate-x-2 transition-all duration-200 pointer-events-none hidden md:block  group-hover:translate-x-0 after:content-[''] after:absolute after:-right-1 after:top-1/2 after:-translate-y-1/2 after:border-y-4 after:border-y-transparent after:border-l-[5px] after:border-l-[#172337]">
                    Scroll to Bottom
                </span>
            </div>
        </div>
    );
};

export default ScrollButtons;
